/**
 * HomePageSkeleton Component
 * 
 * Skeleton loading state for HomePage that mimics the actual page structure:
 * - Hero section with title/subtitle
 * - Search/Chat card
 * - Featured experts grid
 * - Featured publications grid
 * - Map section placeholder
 */

import { cn } from "@/lib/utils";
import { Skeleton } from "@/components/ui/skeleton";
import { Card, CardContent } from "@/components/ui/card";
import { UserCardSkeleton } from "./UserCardSkeleton";
import { PublicationCardSkeleton } from "./PublicationCardSkeleton";
import { GRIDS, RESPONSIVE, LAYOUTS, SPACING } from "@/design-system";

interface HomePageSkeletonProps {
  className?: string;
  /** Show map section skeleton */
  showMap?: boolean;
}

export function HomePageSkeleton({ 
  className,
  showMap = true,
}: HomePageSkeletonProps) {
  return (
    <div className={cn("flex-1 bg-muted/30", className)}>
      {/* Hero Section Skeleton */}
      <section className={LAYOUTS.heroSection}>
        <div className={RESPONSIVE.spaceYResponsive}>
          {/* Title - large centered */}
          <Skeleton className="h-10 sm:h-12 md:h-14 w-3/4 mx-auto" />
          {/* Subtitle - centered */}
          <Skeleton className="h-5 sm:h-6 w-1/2 mx-auto" />
        </div>
        
        {/* Search/Chat Card Skeleton */}
        <Card className="max-w-2xl mx-auto mt-8 sm:mt-12">
          <CardContent className={RESPONSIVE.paddingCardContentWide}>
            <div className={RESPONSIVE.spaceYResponsive}>
              <Skeleton className="h-12 sm:h-14 w-full rounded-lg" />
              <Skeleton className="h-10 w-28 sm:w-32" />
            </div>
          </CardContent>
        </Card>
      </section>

      {/* Featured Experts Section Skeleton */}
      <section className={LAYOUTS.sectionCompact}>
        <div className="container">
          {/* Section header */}
          <div className={cn(SPACING.sectionHeader, "text-center")}>
            <Skeleton className="h-7 sm:h-8 w-48 mx-auto" />
            <Skeleton className="h-4 sm:h-5 w-64 mx-auto mt-2" />
          </div>
          {/* Expert cards grid */}
          <div className={GRIDS.featuredGrid}>
            {[1, 2, 3, 4, 5, 6].map(i => (
              <UserCardSkeleton key={i} />
            ))}
          </div>
        </div>
      </section>

      {/* Featured Publications Section Skeleton */}
      <section className={LAYOUTS.sectionCompact}>
        <div className="container">
          {/* Section header */}
          <div className={cn(SPACING.sectionHeader, "text-center")}>
            <Skeleton className="h-7 sm:h-8 w-40 mx-auto" />
            <Skeleton className="h-4 sm:h-5 w-56 mx-auto mt-2" />
          </div>
          {/* Publication cards grid */}
          <div className={GRIDS.featuredGrid}>
            {[1, 2, 3].map(i => (
              <PublicationCardSkeleton key={i} />
            ))}
          </div>
        </div>
      </section>

      {/* Map Section Skeleton */}
      {showMap && (
        <section className={LAYOUTS.sectionCompact}>
          <div className="container">
            <div className={cn(SPACING.sectionHeader, "text-center")}>
              <Skeleton className="h-7 sm:h-8 w-52 mx-auto" />
            </div>
            <Skeleton className="h-64 sm:h-80 md:h-96 w-full rounded-lg" />
          </div>
        </section>
      )}
    </div>
  );
}
